/**
 * FundHeroCard — adaptive hero showing the group fund balance.
 * Two states (per design_guidelines.json → component_specs.fund_balance_hero):
 *
 *  • 0-state  → dashed border, soft surface, single "Top Up" CTA. The empty
 *               illustration / wallet idea, expressed as Premium Void: oversized
 *               wallet icon, generous space, no clutter.
 *  • rich-state → obsidian dark card with white display type. The balance is
 *                 the hero (text-5xl tracking-tighter). Sub-stats reveal as
 *                 small white-on-dark chips. Always shows the legend
 *                 "1 = ₹1 cash" so the unit never causes confusion.
 *
 * iter104: Member-balances strip. A new dim row below the sub-stats grid
 * summarises the membership (count + Σ positive / Σ negative). Tap to expand
 * a top-3 holders + bottom-3 in-debt mini-table. Admin-only inline "Remind"
 * action on red rows. Expansion state persisted to localStorage.
 *
 * Behaviour preserved from the old card: `data-testid="fund-balance"` on the
 * main number; `data-testid="my-coins"` on the user's own balance chip.
 */
import { useState, useMemo } from 'react';
import { Plus, Wallet, ArrowUpRight, ArrowDownRight, Users, ChevronDown, ChevronUp, MessageCircle, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { formatNXC } from '../lib/formatNXC';
import api from '../lib/api';

const EXPAND_KEY = 'fundhero_balances_expanded';

export default function FundHeroCard({
  fundBalance,
  fundData,
  myUserId,
  isAdmin,
  onTopUp,
  members = [],
  groupId,
  onViewAllMembers,
  currency = 'INR',
  holderName = null, // iter241 — the group's cash holder shown next to Cash
  onOpenMoney = null, // iter257 — tap a sub-stat → Money tab for details
}) {
  // iter108: empty-state ONLY when there's truly nothing — no cash AND no
  // receivables / payables. A group with 0 cash but members already in dues
  // should still show the rich net-position view.
  const _rcv = Math.round(fundData?.receivables_from_members ?? 0);
  const _pay = Math.round(fundData?.payables_to_members ?? 0);
  const [showStats, setShowStats] = useState(false);
  const isEmpty = (!fundBalance || fundBalance <= 0) && _rcv === 0 && _pay === 0;

  // ---- 0-state — Premium Void ----
  if (isEmpty) {
    return (
      <motion.section
        data-testid="fund-hero-empty"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-3xl border-2 border-dashed border-border bg-muted/30 px-6 py-10 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-2xl bg-background flex items-center justify-center text-muted-foreground/60 mb-4 shadow-sm">
          <Wallet className="w-7 h-7" strokeWidth={1.75} />
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">
          Group Fund
        </p>
        <p className="font-display text-3xl font-bold tracking-tighter mt-1" data-testid="fund-balance">
          ₹0
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          empty treasury · top up to start
        </p>
        {isAdmin && (
          <Button
            onClick={onTopUp}
            data-testid="fund-hero-topup-btn"
            className="mt-5 rounded-full px-5 h-10 font-bold tracking-tight"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add cash to fund
          </Button>
        )}
        {!isAdmin && (
          <p className="text-xs text-muted-foreground/70 mt-4 max-w-[260px] mx-auto">
            Ask an admin to add the first contribution to start tracking.
          </p>
        )}
      </motion.section>
    );
  }

  // ---- rich-state — Obsidian ----
  // iter108: hero now leads with "Group Net Position" — cash + receivables − payables.
  // Falls back to fund_balance for older /fund responses that don't have net_position.
  const myNxc = Math.round(fundData?.coin_balances?.[myUserId] ?? 0);
  const loansOut = Math.round(fundData?.loans_from_group_outstanding ?? 0);
  const monthExpenses = Math.round(fundData?.month_expenses ?? 0);
  const monthFines = Math.round(fundData?.month_fines ?? 0);
  const monthFees = Math.round(fundData?.month_session_fees ?? 0);
  const monthLabel = new Date().toLocaleString('en-US', { month: 'short' });
  const realCash = Math.round(fundData?.real_cash ?? fundData?.cash_in_hand ?? fundBalance ?? 0);
  const receivable = Math.round(fundData?.receivables_from_members ?? 0);
  const payable = Math.round(fundData?.payables_to_members ?? 0);
  const netPosition = Math.round(
    fundData?.net_position != null
      ? fundData.net_position
      : realCash + receivable - payable,
  );

  return (
    <motion.section
      data-testid="fund-hero-rich"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl bg-zinc-950 text-white px-6 py-7 shadow-2xl"
    >
      {/* Soft brand corner glow — breaks the flatness without adding visual noise */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -right-16 w-64 h-64 rounded-full bg-primary/20 blur-3xl"
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-400 font-bold">
              Group Net Position
            </p>
            <p
              className="font-display text-5xl font-bold tracking-tighter tabular-nums mt-1 break-words"
              data-testid="fund-balance"
              title="Cash on hand + receivables from members − payables to members"
            >
              {formatNXC(netPosition, currency)}
            </p>
            {/* iter108 — Transparent breakdown of the headline. */}
            <p className="text-xs text-zinc-400 mt-1 tabular-nums" data-testid="net-position-breakdown">
              <span title="Real cash sitting in the treasury">
                Cash {formatNXC(realCash, currency)}
                {holderName ? <span className="text-zinc-500" data-testid="cash-holder-label"> · with {holderName}</span> : null}
              </span>
              <span className="text-emerald-400/80"> · to get {formatNXC(receivable, currency)}</span>
              <span className="text-rose-400/80"> · to pay {formatNXC(payable, currency)}</span>
            </p>
          </div>

          <div className="shrink-0">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${
                myNxc < 0
                  ? 'bg-rose-500/15 text-rose-300'
                  : myNxc > 0
                  ? 'bg-emerald-500/15 text-emerald-300'
                  : 'bg-white/5 text-zinc-300'
              }`}
              data-testid="my-coins"
              title="Your personal balance with this group"
            >
              You {myNxc < 0 ? '' : '+'}{formatNXC(myNxc, currency)}
            </span>
          </div>
        </div>

        {/* iter257 — Sub-stats: cash, open loans, and this-month activity. Tap → Money tab.
            iter308 — on phones only "Cash" shows by default; the month stats sit behind
            "More stats" so the tab bar isn't pushed off the first screen. */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-6 pt-5 border-t border-white/10">
          <SubStat label="Cash"                     value={realCash}      accent="primary" Icon={Wallet}         dim={false} currency={currency} onClick={onOpenMoney} testId="stat-cash" title="Real cash in the group's box right now" />
          <button
            type="button"
            onClick={() => setShowStats((v) => !v)}
            data-testid="hero-more-stats"
            className="sm:hidden text-xs font-bold uppercase tracking-[0.16em] text-zinc-400 hover:text-zinc-200 text-left transition-colors"
          >
            {showStats ? 'Hide stats' : 'More stats'}
          </button>
          <div className={`col-span-2 sm:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-2 ${showStats ? '' : 'hidden sm:grid'}`} data-testid="hero-month-stats">
            <SubStat label="Loans out"                value={loansOut}      accent="amber"   Icon={ArrowUpRight}   dim currency={currency} onClick={onOpenMoney} testId="stat-loans-out" title="Outstanding loans the group has given to members" />
            <SubStat label={`Spent · ${monthLabel}`}  value={monthExpenses} accent="rose"    Icon={ArrowUpRight}   dim currency={currency} onClick={onOpenMoney} testId="stat-month-expenses" title="Group expenses this month" />
            <SubStat label={`Fines · ${monthLabel}`}  value={monthFines}    accent="amber"   Icon={ArrowDownRight} dim currency={currency} onClick={onOpenMoney} testId="stat-month-fines" title="Fines charged this month" />
            <SubStat label={`Fees · ${monthLabel}`}   value={monthFees}     accent="emerald" Icon={ArrowDownRight} dim currency={currency} onClick={onOpenMoney} testId="stat-month-fees" title="Session fees collected this month" />
          </div>
        </div>

        {/* iter104 — Member balances strip */}
        <MemberBalancesStrip
          coinBalances={fundData?.coin_balances || {}}
          members={members}
          isAdmin={isAdmin}
          groupId={groupId}
          currency={currency}
          onViewAllMembers={onViewAllMembers}
        />
      </div>
    </motion.section>
  );
}

function SubStat({ label, value, accent, Icon, dim = false, currency = 'INR', onClick = null, testId, title }) {
  const accentClass =
    accent === 'emerald' ? 'text-emerald-300' :
    accent === 'rose'    ? 'text-rose-300'    :
    accent === 'primary' ? 'text-primary'     :
                            'text-amber-300';
  const formatted = formatNXC(value, currency);
  const inner = (
    <>
      <p className={`text-xs uppercase tracking-[0.16em] font-bold flex items-center gap-1 ${dim ? 'text-zinc-500' : 'text-zinc-300'}`}>
        <Icon className={`w-2.5 h-2.5 ${accentClass}`} />
        {label}
      </p>
      <p
        className={`text-sm font-bold tabular-nums tracking-tight mt-0.5 truncate ${dim ? 'text-zinc-200' : 'text-white'}`}
        title={formatted}
      >
        {formatted}
      </p>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        title={title}
        className="min-w-0 text-left rounded-lg -mx-1 px-1 py-0.5 hover:bg-white/5 active:scale-[0.98] transition"
      >
        {inner}
      </button>
    );
  }
  return <div className="min-w-0" data-testid={testId} title={title}>{inner}</div>;
}

function MemberBalancesStrip({ coinBalances, members, isAdmin, groupId, onViewAllMembers, currency = 'INR' }) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(EXPAND_KEY) === '1'; } catch { return false; }
  });
  const [reminding, setReminding] = useState(null);

  // iter108 — colours flipped to GROUP perspective: a member with a negative
  // coin_balance is a receivable for the group (good → green), and a member
  // with a positive coin_balance is a liability for the group (red).
  const { count, receivable, payable, topReceivables, topPayables } = useMemo(() => {
    const memberMap = Object.fromEntries((members || []).map((m) => [m.id, m]));
    const enriched = Object.entries(coinBalances)
      .filter(([uid]) => memberMap[uid])
      .map(([uid, bal]) => ({
        id: uid,
        name: memberMap[uid]?.name || 'Unknown',
        balance: Math.round(bal || 0),
      }));

    let rcv = 0, pay = 0;
    enriched.forEach((m) => {
      if (m.balance < 0) rcv += -m.balance; // receivable from this member
      else if (m.balance > 0) pay += m.balance; // payable to this member
    });

    const receivablesList = enriched
      .filter((m) => m.balance < 0)
      .sort((a, b) => a.balance - b.balance) // most negative first → biggest receivable
      .slice(0, 3);
    const payablesList = enriched
      .filter((m) => m.balance > 0)
      .sort((a, b) => b.balance - a.balance)
      .slice(0, 3);

    return {
      count: enriched.length,
      receivable: rcv,
      payable: pay,
      topReceivables: receivablesList,
      topPayables: payablesList,
    };
  }, [coinBalances, members]);

  if (count === 0) return null;

  const toggle = () => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(EXPAND_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const handleRemind = async (userId, name) => {
    if (!groupId) return;
    setReminding(userId);
    try {
      await api.post(`/whatsapp/send-reminder/${groupId}`, { user_id: userId });
      toast.success(`Reminder sent to ${name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminding(null);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-white/10">
      <button
        type="button"
        onClick={toggle}
        data-testid="fund-hero-balances-toggle"
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 rounded-lg px-1 py-1.5 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <span className="text-xs font-bold text-zinc-300 tabular-nums">
            {count} member{count === 1 ? '' : 's'}
          </span>
          <span className="text-xs text-zinc-500">·</span>
          <span className="text-xs tabular-nums text-emerald-300" title="Members owe the group (receivable)">
            +{formatNXC(receivable, currency)}
          </span>
          <span className="text-xs text-zinc-600">/</span>
          <span className="text-xs tabular-nums text-rose-300" title="Group has to pay members back">
            −{formatNXC(payable, currency)}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="balances-expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            data-testid="fund-hero-balances-panel"
          >
            <div className="grid grid-cols-2 gap-3 pt-3">
              {/* Members who owe the group (receivable) */}
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-emerald-300/80 font-bold flex items-center gap-1 mb-1.5">
                  <ArrowDownRight className="w-2.5 h-2.5" />
                  To pay the group
                </p>
                {topReceivables.length === 0 ? (
                  <p className="text-xs text-zinc-500 italic">All caught up 🎉</p>
                ) : (
                  <ul className="space-y-1">
                    {topReceivables.map((m) => (
                      <li key={m.id} className="flex items-center justify-between gap-2 text-[12px]">
                        <span className="truncate text-zinc-200" title={m.name}>{m.name}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="font-bold tabular-nums text-emerald-300">
                            +{formatNXC(Math.abs(m.balance), currency)}
                          </span>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleRemind(m.id, m.name)}
                              disabled={reminding === m.id}
                              data-testid={`fund-hero-remind-${m.id}`}
                              title="Send WhatsApp reminder"
                              className="rounded-full p-1 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors"
                            >
                              <MessageCircle className="w-3 h-3 text-emerald-200" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Members the group has to pay back */}
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-rose-300/80 font-bold flex items-center gap-1 mb-1.5">
                  <ArrowUpRight className="w-2.5 h-2.5" />
                  Group pays back
                </p>
                {topPayables.length === 0 ? (
                  <p className="text-xs text-zinc-500 italic">No payables</p>
                ) : (
                  <ul className="space-y-1">
                    {topPayables.map((m) => (
                      <li key={m.id} className="flex items-center justify-between gap-2 text-[12px]">
                        <span className="truncate text-zinc-200" title={m.name}>{m.name}</span>
                        <span className="font-bold tabular-nums text-rose-300 shrink-0">
                          {formatNXC(m.balance, currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {onViewAllMembers && (
              <button
                type="button"
                onClick={onViewAllMembers}
                data-testid="fund-hero-view-all-members"
                className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-bold text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                View all in Members
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
